import { useState } from 'react';
import {
  SECURITY_QUESTIONS,
  VERIFY_METHODS,
  defaultVerificationSettings,
  verificationErrors,
  type SecurityQuestionId,
  type VerificationSettings,
  type VerifyMethodId,
} from '@ehr/shared';

/**
 * ehr#309 - Client verification (Chase's VERIFY_SETTINGS_10-08-V3): which methods a client may use
 * to prove who they are before a form opened by QR code or web link fills in their information.
 * Controlled by Data & Settings, whose Save changes stores it (PUT /practice/verification). The
 * verify step on those forms is ehr#310 (Form Builder); a client's security questions are set in
 * the drawer (Demographics → Security Questions, ehr#311).
 */

const SECURITY_CLASS: Record<string, string> = {
  Strong: 'cv-strong',
  Medium: 'cv-med',
  'Medium-low': 'cv-low',
};

/**
 * ehr#310 (Chase 10-13): for now a client proves who they are by first and last name, date of
 * birth and ZIP, what a form's QR code asks. Text a code shows but can't be turned on yet. The
 * other methods, the security questions and the per-form override are hidden; their stored
 * settings are kept, and MORE_OPTIONS brings them back.
 */
const MORE_OPTIONS = false;
const SHOWN_METHODS: VerifyMethodId[] = ['text_code'];
const NAME_DOB_ZIP = {
  name: 'Name, date of birth and ZIP',
  fields: ['First name', 'Last name', 'Date of birth', 'ZIP'],
  security: 'Medium',
};

/** The PUT body: what the practice chose, without the server-side fields. */
export const verificationBody = (s: VerificationSettings) => ({
  methods: s.methods,
  securityQuestions: { offered: s.securityQuestions.offered },
  allowFormOverride: s.allowFormOverride,
});

export function ClientVerificationSettings({
  value,
  onChange,
  canEdit,
}: {
  value: VerificationSettings;
  onChange: (next: VerificationSettings) => void;
  canEdit: boolean;
}) {
  // The mockup's Reset puts every choice back to the defaults; like any change it is kept only
  // when Data & Settings' Save changes is clicked.
  const [resetNote, setResetNote] = useState(false);
  const change = (next: VerificationSettings) => {
    setResetNote(false);
    onChange(next);
  };
  const atDefaults =
    JSON.stringify(verificationBody(value)) ===
    JSON.stringify(verificationBody(defaultVerificationSettings()));
  const errors = verificationErrors(value);
  const methods = VERIFY_METHODS.filter((m) => MORE_OPTIONS || SHOWN_METHODS.includes(m.id));
  // Name, date of birth and ZIP is always on.
  const onCount =
    (MORE_OPTIONS ? 0 : 1) + methods.filter((m) => value.methods[m.id].enabled).length;
  const offered = value.securityQuestions.offered;

  const setMethod = (id: VerifyMethodId, enabled: boolean) =>
    change({ ...value, methods: { ...value.methods, [id]: { enabled } } });
  const setOffered = (next: SecurityQuestionId[]) =>
    change({
      ...value,
      securityQuestions: {
        ...value.securityQuestions,
        // keep the catalog order
        offered: SECURITY_QUESTIONS.map((q) => q.id).filter((id) => next.includes(id)),
      },
    });

  return (
    <div className="cv">
      {!canEdit && <p className="cv-note">Only an admin or manager can change these settings.</p>}

      <div className="cv-card">
        <div className="cv-card-hd">
          <h4>Verification methods</h4>
          <span className="cv-lbl">{onCount} on</span>
        </div>
        <table className="cv-table">
          <thead>
            <tr>
              <th>Method</th>
              <th>Security</th>
              <th className="c">On</th>
            </tr>
          </thead>
          <tbody>
            {!MORE_OPTIONS && (
              <tr data-method="name_dob_zip">
                <td>
                  <div className="cv-name">{NAME_DOB_ZIP.name}</div>
                  <div className="cv-fields">{NAME_DOB_ZIP.fields.join(' · ')}</div>
                  <div className="cv-row-note">
                    Always on: what a client gives when they scan a form&apos;s QR code.
                  </div>
                </td>
                <td className={SECURITY_CLASS[NAME_DOB_ZIP.security]} data-label="Security">
                  <span>{NAME_DOB_ZIP.security}</span>
                </td>
                <td className="c" data-label="On">
                  <label className="sw">
                    <input
                      type="checkbox"
                      id="cv-on-name_dob_zip"
                      aria-label={`${NAME_DOB_ZIP.name} on`}
                      checked
                      disabled
                      readOnly
                    />
                    <span />
                  </label>
                </td>
              </tr>
            )}
            {methods.map((m) => (
              <tr key={m.id} data-method={m.id}>
                <td>
                  <div className="cv-name">{m.name}</div>
                  <div className="cv-fields">{m.fields.join(' · ')}</div>
                  {m.notFunctional && (
                    <div className="cv-row-note">
                      <b>Not functional yet:</b> pending a team decision. Codes will not send.
                    </div>
                  )}
                </td>
                <td className={SECURITY_CLASS[m.security]} data-label="Security">
                  <span title={m.tip}>{m.security}</span>
                </td>
                <td className="c" data-label="On">
                  <label className="sw" title={m.notFunctional ? 'Not available yet' : undefined}>
                    <input
                      type="checkbox"
                      id={`cv-on-${m.id}`}
                      aria-label={`${m.name} on`}
                      checked={value.methods[m.id].enabled}
                      disabled={!canEdit || !!m.notFunctional}
                      onChange={(e) => setMethod(m.id, e.target.checked)}
                    />
                    <span />
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {MORE_OPTIONS && (
          <div className={`cv-err cv-err-row${errors.includes('methods') ? ' on' : ''}`}>
            Turn on at least one method.
          </div>
        )}
      </div>

      {MORE_OPTIONS && value.methods.security_q.enabled && (
        <div className="cv-card" id="cv-questions">
          <div className="cv-card-hd">
            <h4>Security questions</h4>
            <span className="cv-lbl">Client answers {value.securityQuestions.answersRequired}</span>
          </div>
          <div className="cv-qgrid">
            {SECURITY_QUESTIONS.map((q) => (
              <label className="cv-q" key={q.id}>
                <input
                  type="checkbox"
                  id={`cv-q-${q.id}`}
                  checked={offered.includes(q.id)}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setOffered(
                      e.target.checked ? [...offered, q.id] : offered.filter((x) => x !== q.id),
                    )
                  }
                />
                <span>{q.text}</span>
              </label>
            ))}
          </div>
          <div className="cv-qmeta">
            <span>
              <b>{offered.length}</b> of {SECURITY_QUESTIONS.length} offered
            </span>
            {canEdit && (
              <>
                <button
                  type="button"
                  className="cv-link"
                  onClick={() => setOffered(SECURITY_QUESTIONS.map((q) => q.id))}
                >
                  Select all
                </button>
                <button type="button" className="cv-link" onClick={() => setOffered([])}>
                  Clear
                </button>
              </>
            )}
            <span className={`cv-err${errors.includes('questions') ? ' on' : ''}`}>
              Offer at least 2 questions.
            </span>
          </div>
        </div>
      )}

      {MORE_OPTIONS && (
        <div className="cv-card">
          <div className="tog cv-override">
            <div className="tog-txt">
              <div className="t">Allow per-form override in Share form</div>
              <div className="d">Lets a form use different methods than this default.</div>
            </div>
            <label className="sw">
              <input
                type="checkbox"
                id="cv-override"
                aria-label="Allow per-form override in Share form"
                checked={value.allowFormOverride}
                disabled={!canEdit}
                onChange={(e) => change({ ...value, allowFormOverride: e.target.checked })}
              />
              <span />
            </label>
          </div>
        </div>
      )}

      {MORE_OPTIONS && canEdit && (
        <div className="cv-reset">
          {resetNote && (
            <span className="cv-reset-note" role="status">
              Reset to defaults. Not saved yet: click Save changes to keep it.
            </span>
          )}
          <button
            type="button"
            className="btn"
            id="cv-reset"
            disabled={atDefaults}
            onClick={() => {
              onChange({ ...defaultVerificationSettings(), updatedAt: value.updatedAt });
              setResetNote(true);
            }}
          >
            Reset to defaults
          </button>
        </div>
      )}
    </div>
  );
}
